import { PortalLayout } from '@/components/contractor-portal/PortalLayout';
import { PortalRouteGuard } from '@/components/contractor-portal/PortalRouteGuard';
import { RequiredDocumentsChecklist } from '@/components/contractor-portal/RequiredDocumentsChecklist';
import { PortalDocumentUpload } from '@/components/contractor-portal/PortalDocumentUpload';
import { useContractorProfile } from '@/hooks/useContractorProfile';
import { usePartnerSettings, type DocumentType } from '@/hooks/usePartnerSettings';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Clock, CheckCircle2, AlertTriangle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { format, differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export default function ContractorPortalDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile, updateLastLogin } = useContractorProfile();
  const { settings } = usePartnerSettings();
  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [preSelectedType, setPreSelectedType] = useState<string | null>(null);

  useEffect(() => {
    updateLastLogin();
  }, [updateLastLogin]);

  const { data: documents } = useQuery({
    queryKey: ['contractor-documents', profile?.company_id],
    queryFn: async () => {
      if (!profile) return [];
      const { data, error } = await supabase
        .from('partner_documents')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!profile,
  });

  const { data: latestRating } = useQuery({
    queryKey: ['contractor-latest-rating', profile?.company_id],
    queryFn: async () => {
      if (!profile) return null;
      const { data, error } = await supabase
        .from('partner_ratings')
        .select('*')
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!profile,
  });

  // Count unique document types instead of total documents
  const verifiedDocTypes = new Set(
    documents
      ?.filter(d => d.verification_status === 'verified')
      .map(d => d.document_type)
  );
  const verifiedCount = verifiedDocTypes.size;

  const pendingDocTypes = new Set(
    documents
      ?.filter(d => d.verification_status === 'pending')
      .map(d => d.document_type)
  );
  const pendingCount = pendingDocTypes.size;

  const expiringCount = documents?.filter(d => {
    if (!d.expiry_date) return false;
    const daysUntilExpiry = differenceInDays(new Date(d.expiry_date), new Date());
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  }).length || 0;

  const documentTypes = settings?.document_types || [];
  const totalRequired = documentTypes.filter((dt: DocumentType) => dt.required_for_prequalification).length;
  const totalUploaded = documents?.length || 0;

  const handleDocumentClick = (documentTypeName: string) => {
    setPreSelectedType(documentTypeName);
    setUploadDialogOpen(true);
  };

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['contractor-documents', profile?.company_id] });
    setUploadDialogOpen(false);
    setPreSelectedType(null);
  };

  return (
    <PortalRouteGuard>
      <PortalLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Welcome back{profile?.first_name ? `, ${profile.first_name}` : ''}!
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage your documents and track your prequalification status
              </p>
            </div>
            <PortalDocumentUpload 
              open={uploadDialogOpen}
              onOpenChange={setUploadDialogOpen}
              preSelectedDocumentType={preSelectedType}
              onSuccess={handleUploadSuccess}
            />
          </div>

          {/* Required Documents Checklist - Primary focus */}
          <RequiredDocumentsChecklist 
            companyId={profile?.company_id || ''} 
            onDocumentClick={handleDocumentClick}
          />

          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Verified Documents</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{verifiedCount}</div>
                <p className="text-xs text-muted-foreground">
                  {totalRequired - verifiedCount} document types remaining
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
                <Clock className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingCount}</div>
                <p className="text-xs text-muted-foreground">
                  Document types awaiting verification
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
                <AlertTriangle className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{expiringCount}</div>
                <p className="text-xs text-muted-foreground">
                  Within 30 days
                </p>
              </CardContent>
            </Card>
          </div>

          {latestRating && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500" />
                  Latest Performance Rating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold">{latestRating.overall_rating?.toFixed(1)}</p>
                    <p className="text-sm text-muted-foreground">
                      Overall Rating (out of 5.0)
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => navigate('/contractor-portal/ratings')}>
                    View All Ratings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {expiringCount > 0 && (
            <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800 dark:text-orange-200">
                  <AlertTriangle className="h-5 w-5" />
                  Action Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-800 dark:text-orange-200 mb-4">
                  You have {expiringCount} document(s) expiring within the next 30 days. 
                  Upload updated versions to maintain your prequalification status.
                </p>
                <Button onClick={() => navigate('/contractor-portal/documents')}>
                  View Expiring Documents
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <Button variant="outline" className="justify-start" onClick={() => navigate('/contractor-portal/documents')}>
                <FileText className="mr-2 h-4 w-4" />
                View All Documents
              </Button>
              <Button variant="outline" className="justify-start" onClick={() => navigate('/contractor-portal/profile')}>
                Update Profile
              </Button>
              <Button variant="outline" className="justify-start" onClick={() => navigate('/contractor-portal/ratings')}>
                <Star className="mr-2 h-4 w-4" />
                View Ratings
              </Button>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    </PortalRouteGuard>
  );
}
